import Backbone from 'backbone';

import * as BioJS from 'BioJS';
import {Sequence} from 'biojs/Sequence';
import {getUTFlexibleNaturalBasesIfEnabled} from 'modbio/util';

export interface DNASlice {
  strand: 'forward' | 'reverse';
  bases: string;
  offset5: number;
}

interface DNAAttributes {
  _sequence: Sequence;
}

class DNA extends Backbone.Model {
  override initialize(attributes: DNAAttributes, options: unknown): void {
    this.update();
    this.listenTo(this.get('_sequence'), 'change:bases', this.baseChangeHandler.bind(this));
    this.listenTo(
      this.get('_sequence'),
      'change:offset3 change:offset5',
      this.offsetChangeHandler.bind(this)
    );
  }

  forward(): string {
    const seq = this.get('_sequence');
    let fwd = getUTFlexibleNaturalBasesIfEnabled(
      seq.bases(),
      seq.modifiedNucleotides(),
      seq.nucleotideType(),
      seq.get('type')
    );

    const [offset5, offset3] = seq.offsets();
    if (offset5 < 0) {
      fwd = fwd.slice(Math.abs(offset5));
    }
    if (offset3 > 0) {
      fwd = fwd.slice(0, fwd.length - offset3);
    }
    return fwd;
  }

  reverse(): string {
    return BioJS.util.complement(this.reverseNonComp(), this.get('_sequence').baseTable());
  }

  reverseNonComp(): string {
    const [offset5, offset3] = this.get('_sequence').offsets();
    let rev = this.get('_sequence').bases();
    if (offset5 > 0) {
      rev = rev.slice(offset5);
    }
    if (offset3 < 0) {
      rev = rev.slice(0, rev.length + offset3);
    }
    return rev;
  }

  forwardSlice(start: number, end: number): DNASlice {
    let forward;
    const _forward = this.forward();
    const offset5 = this.get('_sequence').offsets()[0];

    if (offset5 < 0) {
      forward = this.sSlice(_forward, start, end + offset5);
    } else {
      forward = this.sSlice(_forward, start, end);
    }

    return {strand: 'forward', bases: forward, offset5};
  }

  reverseSlice(start: number, end: number): DNASlice {
    let reverse;
    const _reverse = this.reverseNonComp();
    const offset5 = this.get('_sequence').offsets()[0];

    if (offset5 > 0) {
      reverse = this.sSlice(_reverse, start, end - offset5);
    } else {
      reverse = this.sSlice(_reverse, start, end);
    }
    reverse = BioJS.util.complement(reverse, this.get('_sequence').baseTable());
    return {strand: 'reverse', bases: reverse, offset5};
  }

  baseChangeHandler(): void {
    this.update();
  }

  offsetChangeHandler(): void {
    this.update();
  }

  update(): void {
    this.trigger('change:forward change:reverse');
  }

  sSlice(str: string, start: number, end: number): string {
    if (start < end) {
      return str.slice(start, end);
    } else {
      return str.slice(start) + str.slice(0, end);
    }
  }

  slice(start: number, end: number): [DNASlice, DNASlice] {
    return [this.forwardSlice(start, end), this.reverseSlice(start, end)];
  }
}

export default DNA;
